import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useAuth } from './useAuth';
import { useLocationStore } from '@/store/location';
import { usePermissions } from './usePermissions';

export interface VirtualSession {
  id: string;
  register_id: string | null;
  register_name: string;
  location_id: string;
  user_id: string;
  opening_balance: number;
  cash_payments: number;
  cash_refunds: number;
  pay_ins: number;
  pay_outs: number;
  status: 'open' | 'paused' | 'closed';
  session_type: 'register' | 'virtual';
  opened_at: string;
  payment_breakdown: Record<string, {
    total: number;
    count: number;
    types: Array<{
      type_id: string | null;
      type_name: string;
      total: number;
      count: number;
    }>;
  }>;
}

/**
 * Hook for managing virtual sessions for admins/managers using POS without a physical register.
 * 
 * Virtual sessions:
 * - Are automatically created when an admin/manager accesses the POS
 * - Track all transactions for accountability
 * - Can be settled just like regular register sessions
 * - One virtual session per user per location per day
 */
export function useVirtualSession() {
  const { user } = useAuth();
  const { currentLocation } = useLocationStore();
  const { effectiveRole } = usePermissions();
  const queryClient = useQueryClient();

  // Check if user is admin/manager (can use virtual sessions)
  const canUseVirtualSession = ['superadmin', 'primary_admin', 'owner', 'admin', 'manager'].includes(
    effectiveRole || ''
  );

  // Get user's active session (register OR virtual)
  const {
    data: activeSession,
    isLoading: sessionLoading,
    error: sessionError,
    refetch: refetchSession,
  } = useQuery({
    queryKey: ['user-active-session', user?.id, currentLocation?.id],
    queryFn: async () => {
      if (!user?.id || !currentLocation?.id) return null;

      const { data, error } = await supabase.rpc('get_user_active_session', {
        p_location_id: currentLocation.id,
      });

      if (error) {
        console.error('Error fetching active session:', error);
        return null;
      }

      // RPC returns an array, get the first row
      return data && data.length > 0 ? (data[0] as VirtualSession) : null;
    },
    enabled: !!user?.id && !!currentLocation?.id,
    staleTime: 1000 * 30, // 30 seconds
    refetchOnWindowFocus: true,
  });

  // Create or get virtual session
  const createVirtualSession = useMutation({
    mutationFn: async () => {
      if (!currentLocation?.id) {
        throw new Error('No location selected');
      }

      if (!canUseVirtualSession) {
        throw new Error('Virtual sessions are only available for managers and admins');
      }

      const { data, error } = await supabase.rpc('get_or_create_virtual_session', {
        p_location_id: currentLocation.id,
      });

      if (error) {
        console.error('Error creating virtual session:', error);
        throw new Error(error.message || 'Failed to create virtual session');
      }

      return data as VirtualSession;
    },
    onSuccess: () => {
      // Refresh the active session query
      queryClient.invalidateQueries({
        queryKey: ['user-active-session', user?.id, currentLocation?.id],
      });
    },
  });

  // Update payment breakdown when a payment is made
  const updatePaymentBreakdown = useMutation({
    mutationFn: async ({
      sessionId,
      paymentMethod,
      amount,
      paymentTypeId,
    }: {
      sessionId: string;
      paymentMethod: string;
      amount: number;
      paymentTypeId?: string | null;
    }) => {
      const { error } = await supabase.rpc('update_session_payment_breakdown', {
        p_session_id: sessionId,
        p_payment_method: paymentMethod,
        p_amount: amount,
        p_payment_type_id: paymentTypeId || null,
      });

      if (error) {
        console.error('Error updating payment breakdown:', error);
        // Don't throw - this is for tracking, shouldn't block payments
      }
    },
  });

  // Get or create a session for the current user
  // Returns existing register session if one exists, otherwise creates/returns virtual session
  const ensureSession = async (): Promise<VirtualSession | null> => {
    // If user already has an active session (register or virtual), use that
    if (activeSession) {
      return activeSession;
    }

    // If user can use virtual sessions, create one
    if (canUseVirtualSession) {
      try {
        const session = await createVirtualSession.mutateAsync();
        return session;
      } catch (error) {
        console.error('Failed to create virtual session:', error);
        return null;
      }
    }

    // User needs a register session but doesn't have one
    return null;
  };

  return {
    // Current session (register or virtual)
    activeSession,
    hasActiveSession: !!activeSession,
    isVirtualSession: activeSession?.session_type === 'virtual',
    isRegisterSession: activeSession?.session_type === 'register',
    
    // Session details
    sessionId: activeSession?.id || null,
    sessionType: activeSession?.session_type || null,
    
    // Loading/error states
    isLoading: sessionLoading,
    error: sessionError,
    
    // Capabilities
    canUseVirtualSession,
    
    // Actions
    createVirtualSession,
    updatePaymentBreakdown,
    ensureSession,
    refetchSession,
    
    // Helper to check if user can proceed with POS
    // Returns true if user has a session or can create a virtual one
    canUsePOS: !!activeSession || canUseVirtualSession,
  };
}

/**
 * Hook to track a payment against the current session.
 * Call this after a payment is successfully processed.
 */
export function useSessionPaymentTracking() {
  const { activeSession, updatePaymentBreakdown } = useVirtualSession();

  const trackPayment = async (
    paymentMethod: string,
    amount: number,
    paymentTypeId?: string | null
  ) => {
    if (!activeSession?.id) {
      console.warn('No active session to track payment');
      return;
    }

    await updatePaymentBreakdown.mutateAsync({
      sessionId: activeSession.id,
      paymentMethod,
      amount,
      paymentTypeId,
    });
  };

  return {
    trackPayment,
    sessionId: activeSession?.id,
    hasSession: !!activeSession,
  };
}

